import { useEffect, useRef, useState } from 'react';
import { fetchProgress } from '../../progress/progress';
import type { ProgramView } from '../../programs/types';

export const CATEGORY_GLYPH: Record<string, string> = {
  Receptive: '👂', Expressive: '🗣️', Echoic: '🔁', Intraverbal: '💬', Mand: '🙋',
  Matching: '🧩', Sorting: '🗂️', Sequencing: '🔢', Math: '➕', Literacy: '🔤',
  'Motor imitation': '🤸', Social: '🤝', 'Daily living': '🪥',
};

const PALETTE = ['#e0603f', '#3a86c8', '#4caf6a', '#8e5cc4', '#f0a929', '#22a39a', '#d24d7c', '#5b6bd6', '#c97b3a', '#2f8fb0'];

export function glyphOf(p: ProgramView): string {
  const text = JSON.stringify(p.config ?? {});
  const m = text.match(/\p{Extended_Pictographic}(️|‍\p{Extended_Pictographic})*/u);
  return m ? m[0] : CATEGORY_GLYPH[p.category ?? ''] ?? '📘';
}

export function colourOfCategory(category: string | null, fallback = 0): string {
  const c = category ?? '';
  let h = 0;
  for (const ch of c) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[(c ? h : fallback) % PALETTE.length];
}

export function motionAllowed(reduceMotion: boolean) {
  return !reduceMotion && window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
}

export type Stage = 0 | 1 | 2 | 3 | 4;

export type Growth = {
  stage: Stage;
  percent: number | null;
  trials: number;
};

export const STAGE_LABEL: Record<Stage, string> = {
  0: 'Not started yet',
  1: 'Just started',
  2: 'Getting there',
  3: 'Nearly learned',
  4: 'Learned',
};

export function growthText(g: Growth | undefined): string {
  if (!g) return 'Checking progress…';
  if (g.stage === 4) return 'Learned';
  if (g.trials === 0) return STAGE_LABEL[0];
  return `${STAGE_LABEL[g.stage]} · ${g.percent ?? 0}% right last time`;
}

function stageOf(p: ProgramView, trials: number, percent: number | null): Stage {
  if (p.track === 'MAINTENANCE') return 4;
  if (trials === 0 || percent === null) return 0;
  if (percent < 50) return 1;
  if (percent < 80) return 2;
  return 3;
}

export function useGrowth(programs: ProgramView[]): Map<string, Growth> {
  const [growth, setGrowth] = useState<Map<string, Growth>>(new Map());
  const key = programs.map((p) => `${p.id}:${p.track}`).join();
  const latest = useRef(programs);
  latest.current = programs;

  useEffect(() => {
    let cancelled = false;
    const list = latest.current;
    void Promise.allSettled(list.map((p) => fetchProgress(p.id, 60))).then((results) => {
      if (cancelled) return;
      const next = new Map<string, Growth>();
      results.forEach((r, i) => {
        const p = list[i];
        if (r.status !== 'fulfilled') { next.set(p.id, { stage: stageOf(p, 0, null), percent: null, trials: 0 }); return; }
        const days = [...r.value.teaching, ...r.value.probes].sort((a, b) => a.day.localeCompare(b.day));
        const last = days.at(-1);
        const percent = last ? last.percent : null;
        next.set(p.id, { stage: stageOf(p, r.value.totalTrials, percent), percent, trials: r.value.totalTrials });
      });
      setGrowth(next);
    });
    return () => { cancelled = true; };
  }, [key]);

  return growth;
}
